import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Dialog } from "component/shared/dialog";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Table, Column, Sort } from "component/shared/table";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getOffMarketListings,
	getOffMarketListingFlags,
	getOffMarketListingsLoading,
	getOffMarketListingsLoaded,
} from "redux/selector";
import {
	fetchOffMarketListings,
	deleteOffMarketListing,
} from "redux/slice/off-market-listings";
import { OffMarketListingResource } from "api/resource/off-market-listing";
import { coldFusionReplatform } from "shared/env";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Off-Market Listings";

const openDetail = (detailUrl: string) => {
	const url = /^https?:\/\//i.test(detailUrl) ? detailUrl : `${env.coldFusionUrl}${detailUrl}`;
	window.open(url, "_blank");
};

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.soldPendingListings}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Home />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/soldseditor.cfm" />}
	/>
);

const ListingsOffMarketBase: React.FC<Props> = ({ user, classes = {}, history, location }) => {
	const dispatch = useDispatch();
	const listings = useSelector((state: RootState) => getOffMarketListings(state));
	const flags = useSelector((state: RootState) => getOffMarketListingFlags(state));
	const loading = useSelector((state: RootState) => getOffMarketListingsLoading(state));
	const loaded = useSelector((state: RootState) => getOffMarketListingsLoaded(state));

	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<Sort>({ id: "cityAddress", desc: false });
	const [toDelete, setToDelete] = useState<OffMarketListingResource | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		dispatch(fetchOffMarketListings() as any);
	}, [dispatch]);

	// Success banner from a `message` redirect param — set by the edit page on save (matches CF's
	// redirect-with-message behavior).
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("message");
		if (message) {
			setSnackbar({ open: true, message, severity: "success" });
		}
	}, [location.search]);

	const notify = (message: string, severity: "success" | "error" = "success") =>
		setSnackbar({ open: true, message, severity });

	const openEdit = (id: number) =>
		history.push(Router.generatePath(paths.listingsOffMarketEdit, { id }));

	const handleDelete = async () => {
		if (!toDelete) {
			return;
		}
		const listing = toDelete;
		setToDelete(null);
		try {
			await dispatch(deleteOffMarketListing({ id: listing.id }) as any).unwrap();
			notify("Sold/Pending listing deleted successfully");
		} catch{
			notify("Failed to delete listing", "error");
		}
	};

	const columns: Column<OffMarketListingResource>[] = [
		{
			id: "cityAddress",
			title: "Address",
			value: (listing) => {
				// Address-first by design: bold street on line 1, city on line 2. Intentional CP
				// divergence from CF (which bolds City first, soldseditor_output_1.cfm:118) — do not
				// re-flip to City-first for parity.
				const streetNumberToken = (listing.streetNumber || "").split(".")[0];
				const streetName = (listing.streetName || "").toUpperCase().substring(0, 18);
				const addressLine = `${streetNumberToken} ${streetName}`.trim();
				return (
					<Mui.Link
						component="button"
						type="button"
						onClick={() => openEdit(listing.id)}
						data-testid={`off-market-edit-${listing.id}`}
						className={classes.cityAddressLink}
					>
						{addressLine && (
							<Mui.Typography variant="body2" className={classes.cityLabel} data-testid={`off-market-address-${listing.id}`}>
								{addressLine}
							</Mui.Typography>
						)}
						<Mui.Typography variant="body2">
							{(listing.city || "").toUpperCase()}
						</Mui.Typography>
					</Mui.Link>
				);
			},
			sort: (a, b) =>
				(a.city || "").localeCompare(b.city || "") ||
				(a.streetName || "").localeCompare(b.streetName || ""),
		},
		{
			id: "status",
			title: "Status",
			value: (listing) => listing.statusLabel,
			sort: (a, b) => (a.statusLabel || "").localeCompare(b.statusLabel || ""),
		},
		{
			id: "showOnWebsite",
			title: "Show On Website",
			value: (listing) => (listing.showOnWebsite ? "Yes" : "No"),
			sort: (a, b) => Number(a.showOnWebsite) - Number(b.showOnWebsite),
		},
		{
			id: "showOnAgentPage",
			title: "Show On Agent Page",
			// MAX-4194 deliberate non-change: gated on featureAgentOffice, NOT agentControl.
			// See the listings-supplemental counterpart for why.
			hidden: !flags.featureAgentOffice,
			value: (listing) => (listing.showOnAgentPage ? "Yes" : "No"),
			sort: (a, b) => Number(a.showOnAgentPage) - Number(b.showOnAgentPage),
		},
		{
			id: "mlsNumberDisplay",
			title: "MLS #",
			value: (listing) => {
				// CF breaks the board short-name and listing number onto two lines
				// (soldseditor_output_1.cfm:124); "Supplemental" (no board) stays one line.
				const display = listing.mlsNumberDisplay || "";
				const idx = display.indexOf(":");
				if (idx === -1) {
					return display;
				}
				return (
					<>
						{display.substring(0, idx + 1)}
						<br />
						{display.substring(idx + 1)}
					</>
				);
			},
			sort: (a, b) => (a.mlsNumberDisplay || "").localeCompare(b.mlsNumberDisplay || ""),
		},
		{
			id: "action",
			title: "Action",
			align: "right",
			value: (listing) => (
				<>
					<Mui.Tooltip title="View listing on your website">
						<Mui.IconButton
							size="small"
							aria-label="View listing on your website"
							onClick={() => openDetail(listing.detailUrl)}
							data-testid={`off-market-show-${listing.id}`}
						>
							<FeatherIcon fontSize="small">
								<Icons.ExternalLink />
							</FeatherIcon>
						</Mui.IconButton>
					</Mui.Tooltip>
					<Mui.IconButton
						size="small"
						onClick={() => setToDelete(listing)}
						data-testid={`off-market-delete-${listing.id}`}
					>
						<FeatherIcon>
							<Icons.Trash />
						</FeatherIcon>
					</Mui.IconButton>
				</>
			),
		},
	];

	const renderAfsMessage = () => (
		<Mui.Grid container direction="column" spacing={2}>
			<Mui.Grid item>
				<MuiAlert severity="info">
					Your Sold/Pending Listings display is set to show automated MLS listing data. To manually manage your
					Sold/Pending listings,{" "}
					<Mui.Link component={RouterLinkWrapper} to={paths.settingsListingResults}>
						turn off automated solds here
					</Mui.Link>
					.
				</MuiAlert>
			</Mui.Grid>
			{/* F6: CF shows the "No Current…" box alongside the automation nudge when AFS is on
				(soldseditor_output_1.cfm:88-89). */}
			<Mui.Grid item>
				<Mui.Typography variant="body2" color="textSecondary">
					No Current Sold/Pending Listings.
				</Mui.Typography>
			</Mui.Grid>
		</Mui.Grid>
	);

	// CF's list uses a client-side DataTables search over the rendered rows; match it with a
	// case-insensitive filter across address + MLS number (soldseditor_output_1.cfm .data-table).
	const filteredListings = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) {
			return listings;
		}
		return listings.filter((l) =>
			[l.streetNumber, l.streetName, l.city, l.statusLabel, l.mlsNumberDisplay]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(q)
		);
	}, [listings, search]);

	const renderList = () => (
		<Mui.Grid container direction="column" spacing={3}>
			<Mui.Grid item container justifyContent="space-between" alignItems="center" className={classes.headerRow}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">
						Sold/Pending Listings ({listings.length})
					</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Button
						variant="contained"
						color="primary"
						onClick={() => history.push(paths.listingsOffMarketNew)}
						data-testid="off-market-add"
					>
						Add a Sold/Pending
					</Mui.Button>
				</Mui.Grid>
			</Mui.Grid>

			<Mui.Grid item>
				<Mui.Typography variant="body2" color="textSecondary">
					{flags.soldsOnFeaturedYn
						? "These listings are currently set to show on your Featured Properties page. To change this setting to show these listings on a separate Sold/Pending page, "
						: "These listings are currently set to show on a separate Sold/Pending page. To change this setting to show these listings on your Featured Properties page, "}
					{/* CF manual-mode "click here" link to toggle SoldsOnFeatured_YN (soldseditor_output_1.cfm:67-76). */}
					<Mui.Link component={RouterLinkWrapper} to={paths.settingsListingResults}>click here</Mui.Link>.
				</Mui.Typography>
				<Mui.Typography variant="caption" className={classes.note}>
					* Note: Inactive listings that are left for more than 120 days with Status='Unknown' will be deleted.
				</Mui.Typography>
			</Mui.Grid>

			{listings.length > 0 && (
				<Mui.Grid item>
					<Mui.TextField
						variant="outlined"
						size="small"
						placeholder="Search address or MLS #"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<Mui.InputAdornment position="start">
									<FeatherIcon fontSize="small">
										<Icons.Search />
									</FeatherIcon>
								</Mui.InputAdornment>
							),
						}}
						inputProps={{ "data-testid": "off-market-search" }}
					/>
				</Mui.Grid>
			)}

			<Mui.Grid item>
				{listings.length === 0 ? (
					<Mui.Typography variant="body2" color="textSecondary">
						No Current Sold/Pending Listings.
					</Mui.Typography>
				) : filteredListings.length === 0 ? (
					<Mui.Typography variant="body2" color="textSecondary">
						No listings match "{search}".
					</Mui.Typography>
				) : (
					<Table items={filteredListings} columns={columns} sort={sort} onSortChange={setSort} />
				)}
			</Mui.Grid>
		</Mui.Grid>
	);

	const body = () => {
		if (flags.automatedFeaturedSolds) {
			return renderAfsMessage();
		}
		return renderList();
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.soldPendingListings}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				{loading && !loaded ? (
					<Mui.Box display="flex" justifyContent="center" p={4}>
						<Mui.CircularProgress />
					</Mui.Box>
				) : (
					body()
				)}
			</FullPageCard>

			<Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
				<Mui.DialogTitle>Delete Listing</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.DialogContentText>
						Are you sure you want to delete this listing?
					</Mui.DialogContentText>
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button onClick={() => setToDelete(null)} variant="outlined" color="primary">
						No
					</Mui.Button>
					<Mui.Button onClick={handleDelete} variant="contained" color="primary" data-testid="off-market-delete-confirm">
						Yes
					</Mui.Button>
				</Mui.DialogActions>
			</Dialog>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity={snackbar.severity}>
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ListingsOffMarket = withStyles(ListingsOffMarketBase, styles);

const ListingsOffMarketPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ListingsOffMarket {...props} />;
};

export const ListingsOffMarketPage = withStyles(ListingsOffMarketPageBase, styles);
